import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import { ActivityTracker } from "@/components/ActivityTracker";
import { fetchOverrides, fetchAuthorOverrides, wireOverridesRealtime } from "@/lib/bookOverrides";

// Prefetch overrides + wire realtime invalidation once at app boot
fetchOverrides();
fetchAuthorOverrides();
wireOverridesRealtime();

// Lazy-load non-landing routes to reduce initial JS bundle size
const Library = lazy(() => import("./pages/Library"));
const Books = lazy(() => import("./pages/Books"));
const BookDetail = lazy(() => import("./pages/BookDetail"));
const Authors = lazy(() => import("./pages/Authors"));
const AuthorDetail = lazy(() => import("./pages/AuthorDetail"));
const About = lazy(() => import("./pages/About"));
const TeamMemberDetail = lazy(() => import("./pages/TeamMemberDetail"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AuthorDashboard = lazy(() => import("./pages/AuthorDashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const BookVisibilityAudit = lazy(() => import("./pages/BookVisibilityAudit"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ActivityTracker />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/library" element={<Library />} />
              <Route path="/books" element={<Books />} />
              <Route path="/books/:id" element={<BookDetail />} />
              <Route path="/authors" element={<Authors />} />
              <Route path="/authors/:id" element={<AuthorDetail />} />
              <Route path="/what-we-do" element={<About />} />
              <Route path="/about" element={<About />} />
              <Route path="/about/:id" element={<TeamMemberDetail />} />
              <Route path="/testimonials" element={<Testimonials />} />

              <Route path="/pricing" element={<Pricing />} />
              <Route path="/book-visibility-audit" element={<BookVisibilityAudit />} />
              <Route path="/audit" element={<BookVisibilityAudit />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AuthorDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
